"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DescendantProvider = DescendantProvider;
exports.createDescendantContext = createDescendantContext;
exports.useDescendant = useDescendant;
exports.useDescendantKeyDown = useDescendantKeyDown;
exports.useDescendants = useDescendants;
exports.useDescendantsInit = useDescendantsInit;
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _util = require("./util");
const _excluded = ["element", "index"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; } /* eslint-disable callback-return */
const noop = () => {};

/**
 * This hook registers our descendant by passing it into an array. We can then
 * search that array by to find its index when registering it in the component.
 * We use this for focus management, keyboard navigation, and typeahead
 * functionality for some components.
 *
 * The hook accepts the element node and (optionally) a key. The key is useful
 * if multiple descendants have identical text values and we need to
 * differentiate siblings for some reason.
 *
 * Our main goals with this are:
 *   1) maximum composability,
 *   2) minimal API friction
 *   3) SSR compatibility*
 *   4) concurrent safe
 *   5) index always up-to-date with the tree despite changes
 *   6) works with memoization of any component in the tree (hopefully)
 *
 * As for SSR, the good news is that we don't actually need the index on the
 * server for most use-cases, as we are only using it to determine the order of
 * composed descendants for keyboard navigation. However, in the few cases where
 * this is not the case, we can require an explicit index from the app.
 *
 * This is based on reach-ui descendants https://github.com/reach/reach-ui/tree/main/packages/descendants
 *
 * @param {Object} descendant descendant element
 * @param {React.ContextType} context React context
 * @param {number} indexProp index from menu item
 * @returns {number} index value
 */
function useDescendant(descendant, context, indexProp) {
  const forceUpdate = (0, _util.useForceUpdate)();
  const {
    registerDescendant,
    descendants
  } = (0, _react.useContext)(context);

  // This will initially return -1 because we haven't registered the descendant
  // on the first render. After we register, this will then return the correct
  // index on the following render and we will re-register descendants so that
  // everything is up-to-date before the user interacts with a collection.
  const index = indexProp !== null && indexProp !== void 0 ? indexProp : descendants.findIndex(item => item.element === descendant.element);
  (0, _react.useEffect)(() => {
    if (!descendant.element) forceUpdate();
    return registerDescendant(_objectSpread(_objectSpread({}, descendant), {}, {
      index
    }));
  }, [descendant, forceUpdate, index, registerDescendant,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ...Object.values(descendant)]);
  return index;
}

/**
   * Create context for managing descendants
   * @param {String} name descendant name
   * @param {Object} initialValue initial value
   * @returns {React.ContextType} descendant context
   */
function createDescendantContext(name, initialValue) {
  const ctx = /*#__PURE__*/(0, _react.createContext)(_objectSpread({
    descendants: [],
    registerDescendant: () => {}
  }, initialValue));
  ctx.displayName = name;
  return ctx;
}

/**
   * Helper function to get descedants from react context
   * @param {React.ContextType} ctx react context
   * @returns {Array} descendant items
   */
function useDescendants(ctx) {
  return (0, _react.useContext)(ctx).descendants;
}
function useDescendantsInit() {
  return _react.default.useState([]);
}

/**
 * Provides the context for accessing/manipulating descendeants
 * @param {Object} props Prop options
 * @param {React.ContextType} props.context Context that descendants are stored
 * @param {React.ReactNode} props.children Children nodes of the Provider
 * @param {Array} props.items Descendant items
 * @param {React.Dispatch} props.set State set function
 * @returns {React.Provider} Descendant Provider
 */
function DescendantProvider(_ref) {
  let {
    context: Ctx,
    children,
    items,
    set
  } = _ref;
  const registerDescendant = _react.default.useCallback(_ref2 => {
    let {
        element,
        index: explicitIndex
      } = _ref2,
      rest = (0, _objectWithoutProperties2.default)(_ref2, _excluded);
    if (!element) return noop;
    set(collection => {
      if (explicitIndex != null && explicitIndex !== -1) {
        return insertAt(collection, _objectSpread({
          element,
          index: explicitIndex
        }, rest), explicitIndex);
      }
      if (collection.length === 0) {
        // If there are no items, register at index 0 and bail.
        return [_objectSpread(_objectSpread({}, rest), {}, {
          element,
          index: 0
        })];
      }
      const index = findDOMIndex(items, element);
      let newItems = [];
      if (index === -1) {
        newItems = [...collection, _objectSpread(_objectSpread({}, rest), {}, {
          element,
          index: collection.length
        })];
      } else {
        newItems = insertAt(collection, _objectSpread(_objectSpread({}, rest), {}, {
          element,
          index
        }), index);
      }
      return newItems;
    });

    // Cleanup/unregister item
    return () => {
      if (!element) return;
      set(collection => collection.filter(item => element !== item.element));
    };
  },
  // set is a state setter initialized by the useDescendantsInit hook.
  // We can safely ignore the lint warning here because it will not change
  // between renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  return /*#__PURE__*/_react.default.createElement(Ctx.Provider, {
    value: _react.default.useMemo(() => {
      return {
        descendants: items,
        registerDescendant
      };
    }, [items, registerDescendant])
  }, children);
}
DescendantProvider.propTypes = {
  context: _propTypes.default.object.isRequired,
  children: _propTypes.default.node.isRequired,
  items: _propTypes.default.array,
  set: _propTypes.default.func.isRequired
};

/**
 * Key down handler for navigating descendants
 * @param {React.ContextType} context descendant context
 * @param {Object} options keydown options
 * @param {Function} options.callback onActiveItem callback
 * @param {Number} options.activeItemIndex index of the active item
 * @param {String} options.orientation vertical or horizontal orientation of the descendants
 * @param {Function} options.filter function used to filter descendants from being navigated
 * @param {Boolean} options.loop allow looping of descendants navigation
 * @param {Boolean} options.rtl determine if browser is using right to left text
 * @returns {Function} memoized callback for the keyboard event handler
 */
function useDescendantKeyDown(context, options) {
  const descendants = useDescendants(context);
  const {
    callback,
    activeItemIndex,
    orientation = 'vertical',
    filter,
    loop = true,
    rtl = false
  } = options;

  /**
   * Given an array of item objects, a "current" id, and a "step" (1 for forward, -1 for back),
   * this function returns the subsequent id that should now gain focus
   *
   * @param {Array<Object>} items array of item objects
   * @param {String} currentId current item id to step away from
   * @param {Number} step direction (and amount) to step
   * @returns {String} the next id given the step
   */
  // eslint-disable-next-line complexity
  return (0, _react.useCallback)(function handleKeyDown(event) {
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)) {
      return;
    }

    // If we use a filter function, we need to re-index our descendants array
    // so that filtered descendent elements aren't selected.
    const selectableDescendants = filter ? descendants.filter(filter) : descendants;

    // We need some options for any of this to work!
    if (!selectableDescendants.length) {
      return;
    }
    const selectableIndex = selectableDescendants.findIndex(descendant => {
      return descendant.index === activeItemIndex;
    });

    /**
     * Get first selectable descendant
     * @returns {Number} descendant index
     */
    function getFirstItem() {
      return selectableDescendants[0];
    }

    /**
     * Get last selectable descendant
     * @returns {Number} descendant index
     */
    function getLastItem() {
      return selectableDescendants[selectableDescendants.length - 1];
    }

    /**
     * Get next selectable descendant, based on current active index
     * @returns {Number} descendant index
     */
    function getNextItem() {
      const atBottom = activeItemIndex === getLastItem().index;
      const nextItem = selectableDescendants[(selectableIndex + 1) % selectableDescendants.length];
      if (loop) {
        return atBottom ? getFirstItem() : nextItem;
      }
      return atBottom ? selectableDescendants[selectableIndex] : nextItem;
    }

    /**
     * Get previous selectable descendant, based on current active index
     * @returns {Number} descendant index
     */
    function getPreviousItem() {
      const atStart = activeItemIndex === getFirstItem().index;
      const prevItem = selectableDescendants[(selectableIndex - 1) % selectableDescendants.length];
      if (loop) {
        return atStart ? getLastItem() : prevItem;
      }
      return atStart ? selectableDescendants[selectableIndex] : prevItem;
    }
    let item;
    switch (event.key) {
      case 'ArrowDown':
        if (orientation !== 'vertical') return false;
        event.preventDefault();
        event.stopPropagation();
        item = getNextItem();
        break;
      case 'ArrowUp':
        if (orientation !== 'vertical') return false;
        event.preventDefault();
        event.stopPropagation();
        item = getPreviousItem();
        break;
      case 'ArrowRight':
        if (orientation !== 'horizontal') return false;
        event.preventDefault();
        event.stopPropagation();
        item = (rtl ? getPreviousItem : getNextItem)();
        break;
      case 'ArrowLeft':
        if (orientation !== 'horizontal') return false;
        event.preventDefault();
        event.stopPropagation();
        item = (rtl ? getNextItem : getPreviousItem)();
        break;
      case 'Home':
        event.preventDefault();
        event.stopPropagation();
        item = getFirstItem();
        break;
      case 'End':
        event.preventDefault();
        event.stopPropagation();
        item = getLastItem();
        break;
      default:
        // Noop
        return;
    }
    if (item) {
      callback(item);
    }
  }, [descendants, loop, activeItemIndex, callback, orientation, filter, rtl]);
}
function isElementPreceding(a, b) {
  // eslint-disable-next-line no-bitwise
  return Boolean(b.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_PRECEDING);
}
function findDOMIndex(items, element) {
  if (!element) return -1;
  if (!items.length) return -1;
  let length = items.length;
  // Most of the times, the new item will be added at the end of the list, so we
  // do a findIndex in reverse order, instead of wasting time searching the
  // index from the beginning.
  while (length--) {
    const currentElement = items[length].element;
    // eslint-disable-next-line no-continue
    if (!currentElement) continue;
    if (isElementPreceding(currentElement, element)) {
      return length + 1;
    }
  }
  return -1;
}

/**
 * Copy an array of items with a new item added at a specific index.
 * @param {Array} items The source array
 * @param {Object} item The item to insert into the array
 * @param {?Number} index The index to insert the item at
 * @returns {Array} A copy of the array with the item inserted at the specified index
 */
function insertAt(items, item, index) {
  if (index == null || !(index in items)) {
    return [...items, item];
  }
  return [...items.slice(0, index), item, ...items.slice(index)];
}